import { useEffect, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@/app/routing';
import { TABS as PROFILE_TABS } from '@/pages/decedent-profile/constants';
import { TASK_MODULES } from '@/common/constants/constants';

const APP_NAME = 'PAEM After Care';
const DEFAULT_DESCRIPTION =
  'PAEM After Care helps users and administrators manage decedent information, tasks, documents, financial institutions, notes, and account activity in one secure workspace.';

const getProfileTabTitle = (tab, t) => {
  const titles = {
    [PROFILE_TABS.MAIN]: t('Main info'),
    [PROFILE_TABS.RESIDENCE]: t('Residence'),
    [PROFILE_TABS.MARITAL]: t('Marital status'),
    [PROFILE_TABS.DEPENDANTS]: t('Dependants'),
    [PROFILE_TABS.DOCS]: t('Documentation'),
  };

  return titles[tab] || t("Decedent's profile");
};

const getTaskTabTitle = (tab, t) => {
  const titles = {
    [TASK_MODULES.IMMEDIATE]: t('Immediate tasks'),
    [TASK_MODULES.MAIN]: t('Main tasks'),
    [TASK_MODULES.ASSET]: t('Asset tasks'),
    [TASK_MODULES.LEGAL]: t('Legal tasks'),
  };

  return titles[tab] || t('Task list');
};

const getPageTitle = (pathname, t) => {
  const matches = [
    {
      pattern: routes.adminLogin,
      exact: true,
      title: t('Admin Panel Login'),
    },
    {
      pattern: routes.login,
      exact: true,
      title: t('Log in'),
    },
    {
      pattern: '/',
      exact: true,
      title: t('Log in'),
    },
    {
      pattern: routes.setupAccount,
      exact: true,
      title: t('Set up account'),
    },
    {
      pattern: routes.resetPassword,
      exact: true,
      title: t('Reset password'),
    },
    {
      pattern: routes.newPassword,
      exact: true,
      title: t('Create password'),
    },
    {
      pattern: routes.decedentInitiation,
      exact: true,
      title: t('Decedent initiation'),
    },
    {
      pattern: routes.adminPanel.accessCodes,
      exact: true,
      title: t('Access codes'),
    },
    {
      pattern: routes.adminPanel.resellerNew,
      exact: true,
      title: t('New Reseller'),
    },
    {
      pattern: routes.adminPanel.resellerEdit,
      exact: true,
      title: t('Edit Reseller'),
    },
    {
      pattern: routes.adminPanel.resellers,
      exact: true,
      title: t('Resellers'),
    },
    {
      pattern: `${routes.adminPanel.decedentProfile}:id`,
      title: t('Decedent details'),
    },
    {
      pattern: routes.adminPanel.decedents,
      exact: true,
      title: t('Decedent accounts'),
    },
    {
      pattern: routes.dashboard.accountSettings,
      exact: true,
      title: t('Account settings'),
    },
    {
      pattern: routes.dashboard.editQuestions,
      exact: true,
      title: t('Edit questionnaire'),
    },
    {
      pattern: `${routes.dashboard.taskDetails}/:id`,
      title: t('Task details'),
    },
    {
      pattern: `${routes.dashboard.profile}/:tab`,
      getTitle: ({ params }) => getProfileTabTitle(params.tab, t),
    },
    {
      pattern: routes.dashboard.entryLists,
      exact: true,
      title: t('Data entry lists'),
    },
    {
      pattern: routes.dashboard.about,
      exact: true,
      title: t('About'),
    },
    {
      pattern: routes.dashboard.generalNotes,
      exact: true,
      title: t('General Notes'),
    },
    {
      pattern: routes.dashboard.finInstitutionDetails,
      exact: true,
      title: t('Financial Details'),
    },
    {
      pattern: routes.dashboard.finInstitutions,
      exact: true,
      title: t('Financials'),
    },
    {
      pattern: `${routes.dashboard.taskList}/:tab`,
      getTitle: ({ params }) => getTaskTabTitle(params.tab, t),
    },
  ];

  for (const matchConfig of matches) {
    const match = matchPath(pathname, {
      path: matchConfig.pattern,
      exact: matchConfig.exact,
    });

    if (!match) continue;

    return matchConfig.getTitle ? matchConfig.getTitle(match) : matchConfig.title;
  }

  return APP_NAME;
};

const PageMetadata = () => {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  const title = useMemo(() => getPageTitle(pathname, t), [pathname, t, i18n.language]);

  useEffect(() => {
    document.title = title === APP_NAME ? APP_NAME : `${title} - ${APP_NAME}`;

    let metaDescription = document.querySelector('meta[name="description"]');

    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute('content', DEFAULT_DESCRIPTION);
  }, [title]);

  return null;
};

export default PageMetadata;
